import { CalendarOutlined, CloseOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Button, Calendar, DatePicker, Input } from 'antd';
import type { InputRef } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { ChangeEvent, KeyboardEvent as ReactKeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import 'components/Artifacts/ArtifactsExperience.css';

dayjs.extend(customParseFormat);

type ArtifactMemoryDatePickerProps = {
  value?: string | null;
  onChange?: (value: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
};

const DESKTOP_MEDIA_QUERY = '(min-width: 1025px)';
const DISPLAY_FORMAT = 'MM/DD/YYYY';
const STORAGE_FORMAT = 'YYYY-MM-DD';
const MOBILE_INPUT_ERROR_MESSAGE = `Enter a valid date as ${DISPLAY_FORMAT}.`;

type ViewportMetrics = {
  width: number;
  height: number;
  offsetLeft: number;
  offsetTop: number;
};

const getInitialDesktopState = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return true;
  }

  return window.matchMedia(DESKTOP_MEDIA_QUERY).matches;
};

const parsePickerValue = (value?: string | null) => {
  if (!value) {
    return null;
  }

  const parsedValue = dayjs(value);
  return parsedValue.isValid() ? parsedValue : null;
};

const parseTypedPickerValue = (value: string) => {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return null;
  }

  const parsedValue = dayjs(trimmedValue, DISPLAY_FORMAT, true);
  return parsedValue.isValid() ? parsedValue : null;
};

const getViewportMetrics = (): ViewportMetrics => {
  if (typeof window === 'undefined') {
    return {
      width: 0,
      height: 0,
      offsetLeft: 0,
      offsetTop: 0,
    };
  }

  const { visualViewport } = window;

  if (visualViewport) {
    return {
      width: visualViewport.width,
      height: visualViewport.height,
      offsetLeft: visualViewport.offsetLeft,
      offsetTop: visualViewport.offsetTop,
    };
  }

  return {
    width: window.innerWidth,
    height: window.innerHeight,
    offsetLeft: 0,
    offsetTop: 0,
  };
};

export function ArtifactMemoryDatePicker({
  value = null,
  onChange,
  placeholder = DISPLAY_FORMAT,
  disabled = false
}: ArtifactMemoryDatePickerProps) {
  const [isDesktop, setIsDesktop] = useState(getInitialDesktopState);
  const [isMobileSheetOpen, setIsMobileSheetOpen] = useState(false);
  const pickerValue = useMemo(() => parsePickerValue(value), [value]);
  const [draftValue, setDraftValue] = useState<Dayjs | null>(pickerValue);
  const [panelValue, setPanelValue] = useState<Dayjs>(pickerValue ?? dayjs());
  const [viewportMetrics, setViewportMetrics] = useState<ViewportMetrics>(getViewportMetrics);
  const [mobileInputValue, setMobileInputValue] = useState(pickerValue ? pickerValue.format(DISPLAY_FORMAT) : '');
  const [mobileInputError, setMobileInputError] = useState<string | null>(null);
  const mobileTriggerRef = useRef<InputRef | null>(null);
  const mobileSheetInputRef = useRef<InputRef | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQueryList = window.matchMedia(DESKTOP_MEDIA_QUERY);
    const handleMediaQueryChange = (event: MediaQueryListEvent) => {
      setIsDesktop(event.matches);
    };

    setIsDesktop(mediaQueryList.matches);

    mediaQueryList.addEventListener('change', handleMediaQueryChange);

    return () => {
      mediaQueryList.removeEventListener('change', handleMediaQueryChange);
    };
  }, []);

  useEffect(() => {
    if (isDesktop) {
      setIsMobileSheetOpen(false);
    }
  }, [isDesktop]);

  useEffect(() => {
    if (isMobileSheetOpen) {
      return;
    }

    setDraftValue(pickerValue);
    setPanelValue(pickerValue ?? dayjs());
    setMobileInputValue(pickerValue ? pickerValue.format(DISPLAY_FORMAT) : '');
    setMobileInputError(null);
  }, [isMobileSheetOpen, pickerValue]);

  useEffect(() => {
    if (!isMobileSheetOpen || typeof window === 'undefined') {
      return undefined;
    }

    const syncViewportMetrics = () => {
      setViewportMetrics(getViewportMetrics());
    };

    syncViewportMetrics();

    const { visualViewport } = window;
    visualViewport?.addEventListener('resize', syncViewportMetrics);
    visualViewport?.addEventListener('scroll', syncViewportMetrics);
    window.addEventListener('resize', syncViewportMetrics);

    return () => {
      visualViewport?.removeEventListener('resize', syncViewportMetrics);
      visualViewport?.removeEventListener('scroll', syncViewportMetrics);
      window.removeEventListener('resize', syncViewportMetrics);
    };
  }, [isMobileSheetOpen]);

  useEffect(() => {
    if (!isMobileSheetOpen || typeof document === 'undefined' || isDesktop) {
      return undefined;
    }

    const { body, documentElement } = document;
    const scrollY = window.scrollY;
    const originalBodyStyles = {
      overflow: body.style.overflow,
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      touchAction: body.style.touchAction,
    };
    const originalHtmlOverflow = documentElement.style.overflow;

    documentElement.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.touchAction = 'none';

    return () => {
      documentElement.style.overflow = originalHtmlOverflow;
      body.style.overflow = originalBodyStyles.overflow;
      body.style.position = originalBodyStyles.position;
      body.style.top = originalBodyStyles.top;
      body.style.left = originalBodyStyles.left;
      body.style.right = originalBodyStyles.right;
      body.style.width = originalBodyStyles.width;
      body.style.touchAction = originalBodyStyles.touchAction;

      if (scrollY > 0) {
        window.scrollTo(0, scrollY);
      }
    };
  }, [isDesktop, isMobileSheetOpen]);

  useEffect(() => {
    if (!isMobileSheetOpen || typeof document === 'undefined') {
      return undefined;
    }

    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMobileSheetOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMobileSheetOpen]);

  const emitChange = (nextDate: Dayjs | null) => {
    onChange?.(nextDate ? nextDate.format(STORAGE_FORMAT) : null);
  };

  const openMobileSheet = () => {
    if (disabled) {
      return;
    }

    setDraftValue(pickerValue);
    setPanelValue(pickerValue ?? dayjs());
    setMobileInputValue(pickerValue ? pickerValue.format(DISPLAY_FORMAT) : '');
    setMobileInputError(null);
    setViewportMetrics(getViewportMetrics());
    setIsMobileSheetOpen(true);
  };

  const closeMobileSheet = () => {
    setIsMobileSheetOpen(false);
    mobileTriggerRef.current?.focus();
  };

  const syncMobileInputValue = (focusOnInvalid = false) => {
    const parsedValue = parseTypedPickerValue(mobileInputValue);

    if (mobileInputValue.trim() && !parsedValue) {
      setMobileInputError(MOBILE_INPUT_ERROR_MESSAGE);

      if (focusOnInvalid) {
        mobileSheetInputRef.current?.focus();
      }

      return { isValid: false as const, value: null };
    }

    setMobileInputError(null);

    if (!parsedValue) {
      setDraftValue(null);
      setMobileInputValue('');
      return { isValid: true as const, value: null };
    }

    setDraftValue(parsedValue);
    setPanelValue(parsedValue);
    setMobileInputValue(parsedValue.format(DISPLAY_FORMAT));

    return { isValid: true as const, value: parsedValue };
  };

  const commitDraftValue = () => {
    const resolvedInput = syncMobileInputValue(true);

    if (!resolvedInput.isValid) {
      return;
    }

    const nextValue = resolvedInput.value ? resolvedInput.value.format(STORAGE_FORMAT) : null;
    const currentValue = pickerValue ? pickerValue.format(STORAGE_FORMAT) : null;

    if (nextValue !== currentValue) {
      onChange?.(nextValue);
    }

    closeMobileSheet();
  };

  const clearMobileDate = () => {
    emitChange(null);
    setDraftValue(null);
    setPanelValue(dayjs());
    setMobileInputValue('');
    setMobileInputError(null);
    closeMobileSheet();
  };

  const handleMobileInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;

    setMobileInputValue(nextValue);
    setMobileInputError(null);

    const parsedValue = parseTypedPickerValue(nextValue);

    if (parsedValue) {
      setDraftValue(parsedValue);
      setPanelValue(parsedValue);
    } else if (!nextValue.trim()) {
      setDraftValue(null);
    }
  };

  const handleMobileInputBlur = () => {
    syncMobileInputValue();
  };

  const handleMobileTriggerKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openMobileSheet();
    }
  };

  const calendarValue = draftValue ?? panelValue;
  const mobileSheet =
    !isDesktop && isMobileSheetOpen && typeof document !== 'undefined'
      ? createPortal(
          <div
            className="artifact-memory-date-picker__sheet-root"
            data-testid="artifact-memory-date-picker-sheet-root"
            style={{
              left: viewportMetrics.offsetLeft,
              top: viewportMetrics.offsetTop,
              width: viewportMetrics.width,
              height: viewportMetrics.height,
            }}
          >
            <button
              type="button"
              className="artifact-memory-date-picker__sheet-backdrop"
              aria-label="Close memory date picker"
              onClick={closeMobileSheet}
            />

            <div
              aria-label="Select memory date"
              aria-modal="true"
              className="artifact-memory-date-picker__sheet"
              data-testid="artifact-memory-date-picker-sheet"
              role="dialog"
            >
              <div className="artifact-memory-date-picker__sheet-header">
                <div className="artifact-memory-date-picker__sheet-title">Select memory date</div>
                <button
                  type="button"
                  aria-label="Close memory date picker"
                  className="artifact-memory-date-picker__sheet-close"
                  onClick={closeMobileSheet}
                >
                  <CloseOutlined />
                </button>
              </div>

              <div className="artifact-memory-date-picker__sheet-input-group">
                <Input
                  ref={mobileSheetInputRef}
                  autoFocus
                  aria-label="Memory date"
                  className="artifact-memory-date-picker artifact-memory-date-picker__sheet-input"
                  inputMode="numeric"
                  onBlur={handleMobileInputBlur}
                  onChange={handleMobileInputChange}
                  placeholder={DISPLAY_FORMAT}
                  size="large"
                  status={mobileInputError ? 'error' : undefined}
                  value={mobileInputValue}
                />
{mobileInputError ? (
  <div className="artifact-memory-date-picker__sheet-input-error" role="alert" aria-live="polite">
    {mobileInputError}
  </div>
) : null}
              </div>

              <Calendar
                className="artifact-memory-date-picker__calendar"
                data-testid="artifact-memory-date-picker-calendar"
                fullscreen={false}
                headerRender={({ value: currentValue, onChange: onCalendarChange }) => (
                  <div className="artifact-memory-date-picker__calendar-header">
                    <button
                      type="button"
                      aria-label="Previous month"
                      className="artifact-memory-date-picker__calendar-nav"
                      onClick={() => {
                        const nextValue = currentValue.subtract(1, 'month');
                        setPanelValue(nextValue);
                        onCalendarChange(nextValue);
                      }}
                    >
                      <LeftOutlined />
                    </button>

                    <div className="artifact-memory-date-picker__calendar-label">{currentValue.format('MMMM YYYY')}</div>

                    <button
                      type="button"
                      aria-label="Next month"
                      className="artifact-memory-date-picker__calendar-nav"
                      onClick={() => {
                        const nextValue = currentValue.add(1, 'month');
                        setPanelValue(nextValue);
                        onCalendarChange(nextValue);
                      }}
                    >
                      <RightOutlined />
                    </button>
                  </div>
                )}
                onPanelChange={(nextValue) => setPanelValue(nextValue)}
                onSelect={(nextValue) => {
                  setDraftValue(nextValue);
                  setPanelValue(nextValue);
                  setMobileInputValue(nextValue.format(DISPLAY_FORMAT));
                  setMobileInputError(null);
                }}
                value={calendarValue}
              />

              <div className="artifact-memory-date-picker__sheet-footer">
                <Button onClick={clearMobileDate} size="large">
                  Clear
                </Button>
                <Button onClick={commitDraftValue} size="large" type="primary">
                  Done
                </Button>
              </div>
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <>
      {isDesktop ? (
        <DatePicker
          allowClear
          className="artifact-memory-date-picker"
          disabled={disabled}
          format={DISPLAY_FORMAT}
          onChange={(nextDate) => emitChange(nextDate)}
          placeholder={placeholder}
          size="large"
          suffixIcon={<CalendarOutlined />}
          value={pickerValue}
        />
      ) : (
        <Input
          aria-expanded={isMobileSheetOpen}
          aria-haspopup="dialog"
          className="artifact-memory-date-picker artifact-memory-date-picker__mobile-trigger"
          disabled={disabled}
          onClick={openMobileSheet}
          onKeyDown={handleMobileTriggerKeyDown}
          placeholder={placeholder}
          readOnly
          ref={mobileTriggerRef}
          size="large"
          suffix={<CalendarOutlined />}
          value={pickerValue ? pickerValue.format(DISPLAY_FORMAT) : ''}
        />
      )}

      {mobileSheet}
    </>
  );
}

export default ArtifactMemoryDatePicker;
